import { Component, ElementRef, HostBinding, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatFormFieldAppearance, FloatLabelType } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { SharedStandaloneModule } from 'app/shared/shared-standalone.module';
import { COUNTRY_CODE_OPTIONS, CountryCodeOption, DEFAULT_COUNTRY_CODE } from 'app/shared/constants/country-codes';

@Component({
    selector: 'app-phone-field',
    standalone: true,
    imports: [...SharedStandaloneModule.getFormModules()],
    templateUrl: './phone-field.component.html',
    styleUrls: ['./phone-field.component.scss'],
})
export class PhoneFieldComponent {
    @Input({ required: true }) ddiControl!: FormControl<string | null>;
    @Input({ required: true }) phoneControl!: FormControl<string | null>;
    @Input() label = 'Telefone';
    @Input() required = false;
    @Input() compact = false;
    /** fill = mesmo visual dos formulários compactos do portal solicitante */
    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Input() floatLabel: FloatLabelType = 'auto';
    @Input() placeholder = '(00) 0000-0000 ou (00) 00000-0000';

    @HostBinding('class.phone-field--fill')
    get isFillAppearance(): boolean {
        return this.appearance === 'fill';
    }

    @HostBinding('class.phone-field--compact-unified')
    get isCompactUnified(): boolean {
        return this.compact && this.appearance !== 'fill';
    }
    /** Classes CSS do painel do DDI (ex.: tema escuro no iframe). */
    @Input() ddiPanelClass = 'phone-field-ddi-panel';
    /** Opcional: quando fornecido, exibe um ícone de WhatsApp clicável dentro do input que alterna esse controle (0/1). */
    @Input() whatsappControl?: FormControl<number | null>;

    @ViewChild('ddiSelect') ddiSelect?: MatSelect;
    @ViewChild('ddiSearchInput') ddiSearchInput?: ElementRef<HTMLInputElement>;

    readonly defaultCountryCode = DEFAULT_COUNTRY_CODE;
    readonly allCountryCodeOptions = COUNTRY_CODE_OPTIONS;
    private readonly brPhoneMask = '(00) 0000-0000||(00) 00000-0000';
    private readonly ptPhoneMask = '000 000 000';
    private readonly internationalPhoneMask = '0*';

    ddiSearchTerm = '';
    filteredCountryCodeOptions: CountryCodeOption[] = [...COUNTRY_CODE_OPTIONS];

    get selectPanelClass(): string {
        return ['phone-field-ddi-panel', this.ddiPanelClass].filter(Boolean).join(' ');
    }

    get isBrazilDdi(): boolean {
        const ddi = this.ddiControl?.value || this.defaultCountryCode;
        return ddi === '+55';
    }

    get isPortugalDdi(): boolean {
        return (this.ddiControl?.value || '') === '+351';
    }

    get phoneMask(): string {
        if (this.isBrazilDdi) return this.brPhoneMask;
        if (this.isPortugalDdi) return this.ptPhoneMask;
        return this.internationalPhoneMask;
    }

    get phonePlaceholder(): string {
        if (this.placeholder && this.placeholder !== '(00) 0000-0000 ou (00) 00000-0000') {
            return this.placeholder;
        }
        if (this.compact) {
            if (this.isBrazilDdi) return '(00) 00000-0000';
            if (this.isPortugalDdi) return '900 000 000';
            return 'Número';
        }
        if (this.isBrazilDdi) return '(00) 0000-0000 ou (00) 00000-0000';
        if (this.isPortugalDdi) return '900 000 000';
        return 'Apenas números';
    }

    get selectedCountryFlag(): string {
        return this.flagEmojiForDdi(this.ddiControl?.value || this.defaultCountryCode);
    }

    flagEmoji(opt: CountryCodeOption): string {
        return this.flagEmojiForDdi(opt.value);
    }

    onDdiPanelOpened(): void {
        this.ddiSearchTerm = '';
        this.applyDdiFilter('');
        // MDC scrolla o item selecionado para o meio e cria um “vão” acima do search.
        const pinSearchToTop = () => {
            const panel = document.querySelector('.phone-field-ddi-panel') as HTMLElement | null;
            if (panel) {
                panel.scrollTop = 0;
            }
            this.ddiSearchInput?.nativeElement?.focus();
        };
        setTimeout(pinSearchToTop, 0);
        setTimeout(pinSearchToTop, 40);
        setTimeout(pinSearchToTop, 120);
    }

    onDdiSearchInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value ?? '';
        this.ddiSearchTerm = value;
        this.applyDdiFilter(value);
    }

    onDdiSearchKeydown(event: KeyboardEvent): void {
        event.stopPropagation();
    }

    onDdiSearchClick(event: MouseEvent): void {
        event.stopPropagation();
    }

    onPhoneInputInteract(event: Event): void {
        event.stopPropagation();
    }

    get isWhatsapp(): boolean {
        const value = this.whatsappControl?.value as unknown;
        return value === 1 || value === true;
    }

    toggleWhatsapp(): void {
        if (!this.whatsappControl || this.whatsappControl.disabled) return;
        this.whatsappControl.setValue(this.isWhatsapp ? 0 : 1);
        this.whatsappControl.markAsDirty();
    }

    private applyDdiFilter(term: string): void {
        const normalized = term.trim().toLowerCase().replace(/^\+/, '');
        if (!normalized) {
            this.filteredCountryCodeOptions = this.allCountryCodeOptions;
            return;
        }
        this.filteredCountryCodeOptions = this.allCountryCodeOptions.filter((opt) => opt.searchText.includes(normalized));
    }

    private flagEmojiForDdi(ddi: string): string {
        const option = this.allCountryCodeOptions.find((item) => item.value === ddi);
        if (!option?.iso || option.iso.length !== 2) {
            return '';
        }

        const codePoints = [...option.iso.toUpperCase()].map((char) => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }
}
